import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { TokenService } from '../services/token.service';
import { environment } from 'src/environments/environment';
import { targetsOurApi } from './api-target';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private tokenService: TokenService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if this is a retry attempt (marked with custom header)
    const isRetry = request.headers.has('X-Token-Retry');

    request = this.addClientVersionHeader(request);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Only attempt token refresh if:
        // 1. Status is 401
        // 2. Not already a retry attempt (prevents infinite loop)
        // 3. Not an authentication endpoint
        if (error.status === 401 &&
            !isRetry &&
            !request.url.includes('/token/') &&
            !request.url.includes('/oauth2/')) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenService.refreshToken().pipe(
        switchMap((token: string | null) => {
          this.isRefreshing = false;
          if (token) {
            this.refreshTokenSubject.next(token);
            // Mark this as a retry to prevent infinite loop if 401 persists
            return next.handle(this.addAuthHeader(request, token, true));
          }
          // Token refresh failed, user needs to re-authenticate
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.tokenService.clearToken();
          // Optionally redirect to login
          return throwError(() => err);
        })
      );
    } else {
      // Wait for token refresh to complete
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addAuthHeader(request, token!, true)))
      );
    }
  }

  /**
   * Announce this build's version to our own API.
   *
   * Load-bearing, not telemetry: the API gates consent on `X-Client-Version >=
   * CONSENT_MIN_CLIENT_VERSION`, so a build that ships the acceptance UI must say so or it is
   * treated as a legacy client and left in the grace window until the API's enforcement date.
   *
   * Applied here rather than in `HttpService` because several of its methods hand-roll their own
   * headers, and a request that silently omits this is a request that silently skips the gate.
   * Scoped through `targetsOurApi` so it never reaches MercadoPago, KLAP, Google or Firebase —
   * a custom header on a cross-origin request forces a CORS preflight those endpoints need not
   * answer.
   */
  private addClientVersionHeader(request: HttpRequest<any>): HttpRequest<any> {
    if (!targetsOurApi(request.url)) {
      return request;
    }
    return request.clone({
      setHeaders: { 'X-Client-Version': environment.appVersion },
    });
  }

  private addAuthHeader(request: HttpRequest<any>, token: string, isRetry: boolean = false): HttpRequest<any> {
    const headers: any = {
      Authorization: `Bearer ${token}`
    };

    // Mark as retry to prevent infinite loop
    if (isRetry) {
      headers['X-Token-Retry'] = 'true';
    }

    return request.clone({
      setHeaders: headers
    });
  }
}
